import { useState, useEffect } from "react";
import { useUpdateStockOut, getListStockOutQueryKey, type StockOut } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { stkOutId } from "@/lib/display-id";

/**
 * Edit a stock-out row's safe (non-money) fields:
 *   - transactionDate: shift to correct date (D+1 grace enforced server-side)
 *   - reason: free-text notes
 *
 * Money-affecting fields (quantity_issued, item_id, contact_id,
 * consumption_type) are NOT editable here — those still require
 * DELETE + re-create so stock and member_charge revert atomically
 * (Phase-55 cascade + Phase-39 charge unwind).
 *
 * Server 409s on rows older than 24 hours from now with
 * code=stock_out_edit_window_expired; client also guards preemptively.
 */
export function EditStockOutDialog({
  row,
  open,
  onClose,
  onSaved,
}: {
  row: StockOut | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const updateMut = useUpdateStockOut();

  const [dateStr, setDateStr] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    if (!row) return;
    const iso = new Date(row.transactionDate).toISOString().slice(0, 10);
    setDateStr(iso);
    setReason((row as { reason?: string | null }).reason ?? "");
  }, [row]);

  if (!row) return null;

  const rowTs = new Date(row.transactionDate).getTime();
  const graceCutoff = Date.now() - 24 * 60 * 60 * 1000;
  const withinGrace = rowTs >= graceCutoff;

  async function onSave() {
    if (!row) return;
    if (!withinGrace) {
      toast({
        title: "Edit window expired",
        description: "This entry is older than 24 hours. Delete + re-create instead.",
        variant: "destructive",
      });
      return;
    }
    try {
      // Anchor the new date at noon IST to avoid TZ midnight-flip surprises.
      const isoDate = `${dateStr}T12:00:00+05:30`;
      await updateMut.mutateAsync({
        id: row.id,
        data: {
          transactionDate: isoDate,
          reason: reason.trim() ? reason.trim() : null,
        },
      });
      await qc.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      toast({ title: `${stkOutId(row.id)} updated.` });
      onSaved?.();
      onClose();
    } catch (err) {
      const anyErr = err as { response?: { data?: { hint?: string; error?: string } }; message?: string };
      const msg = anyErr?.response?.data?.hint ?? anyErr?.response?.data?.error ?? anyErr?.message ?? "Update failed";
      toast({ title: msg, variant: "destructive" });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {stkOutId(row.id)}</DialogTitle>
          <DialogDescription>
            Adjust the transaction date or add a note. To change item, quantity, or member, delete this entry and re-create it (stock + charge revert atomically).
          </DialogDescription>
        </DialogHeader>

        {!withinGrace && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            This entry is older than 24 hours. The edit window has expired — delete + re-create instead.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Transaction date</label>
            <Input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              disabled={!withinGrace || updateMut.isPending}
            />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Note (optional)</label>
            <Textarea
              rows={3}
              placeholder="e.g. corrected date — operator originally clicked wrong day"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!withinGrace || updateMut.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updateMut.isPending}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!withinGrace || updateMut.isPending}>
            {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
